import { z } from 'zod';
import { userRoleSchema } from '../constants/roles.js';
import { addShopDays, shopDaySchema, shopDayStart } from '../reports/date-ranges.js';
import {
  dateInputSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
} from './common.js';

/**
 * THE HR LAYER AROUND ATTENDANCE — overtime, permission, lateness and leave.
 *
 * The roster in `attendance.ts` answers "who was in today". That is the easy half. What a shop
 * actually argues about at the end of the month is the other half: the two hours Kavitha stayed
 * back on Thursday, the ninety minutes Ravi took to go to the bank, the four days somebody asked
 * for and nobody wrote down. Until now every one of those lived in a notebook or in somebody's
 * memory, which is to say it lived in whoever was more confident on payday.
 *
 * Three decisions shape this file, and each of them is a decision NOT to do the obvious thing.
 *
 * **Lateness is derived; overtime and permission are recorded.** It is tempting to make all
 * three ledgers. But lateness already has an answer — the arrival stamp against the shift — and
 * a stored "late minutes" column would immediately disagree with it the first time a shop
 * widened its grace period. Overtime and permission have no such stamp to derive from: staying
 * back is not a check-out, and stepping out at three is not a check-in. They are entries because
 * somebody has to say they happened.
 *
 * **No rupees anywhere in this file.** Juvee's OT ledger carries an "OT pay" column somebody
 * types in. Ours carries minutes and a reason, and the pricing stays where pricing already
 * lives — the payroll run and its add-ons, which resolve a rate from the rate card rather than
 * from whoever was filling in the form. That is also what lets a karigar READ their own overtime
 * ledger: it is a record of their hours, not of their wage, so it does not answer to the rule
 * that keeps a tailor away from money.
 *
 * **Approving leave WRITES the attendance.** A request that is approved and then still has to be
 * marked on the roster by hand is a request nobody trusts — the shop ends up marking leave twice
 * or not at all. Approval marks every day in the range `on_leave` with the reason carried
 * across, which is the whole point of asking for approval through the app rather than by phone.
 */

/* -------------------------------------------------------------------------------------------
 * Shift timings
 * ---------------------------------------------------------------------------------------- */

/** A 24-hour `HH:MM` clock time — what `<input type="time">` produces. */
const clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour HH:MM time');

/**
 * One person's own hours, when they differ from the shop's.
 *
 * The shop-wide `workdayStart`/`workdayEnd` in the attendance settings stay the default and the
 * fallback; this overrides them for one person. That is the shape a real shop has: a cutting
 * master who comes in at eight, one finisher who works afternoons, and everybody else on the
 * shop's own hours — and making every shop fill in forty shift rows to express "the same as the
 * shop, except him" would be a screen nobody completes.
 *
 * `graceMinutes` is separately nullable INSIDE the shift, so a shop can say "she starts at eight"
 * without also having to restate the grace period it already agreed with everybody.
 */
export const staffShiftSchema = z
  .object({
    start: clockTimeSchema,
    end: clockTimeSchema,
    /** Overrides the shop's grace period for this person. `null` keeps the shop's. */
    graceMinutes: z.number().int().min(0).max(180).nullish(),
  })
  .refine((value) => value.end > value.start, {
    message: 'The shift must end after it starts',
    path: ['end'],
  });
export type StaffShift = z.infer<typeof staffShiftSchema>;

/** What actually applies to one person on one day: their shift, or the shop's hours. */
export interface ResolvedShift {
  readonly start: string;
  readonly end: string;
  readonly graceMinutes: number;
  /** True when this came from the person's own shift rather than from the shop's hours. */
  readonly personal: boolean;
}

export function resolveShift(
  shopHours: { workdayStart: string; workdayEnd: string; graceMinutes: number },
  shift: StaffShift | null | undefined,
): ResolvedShift {
  if (!shift) {
    return {
      start: shopHours.workdayStart,
      end: shopHours.workdayEnd,
      graceMinutes: shopHours.graceMinutes,
      personal: false,
    };
  }
  return {
    start: shift.start,
    end: shift.end,
    graceMinutes: shift.graceMinutes ?? shopHours.graceMinutes,
    personal: true,
  };
}

/** Minutes from midnight for an `HH:MM`. */
export function clockMinutes(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * How many minutes after the grace period somebody arrived. Zero when they were on time, and
 * zero when they never arrived — an absence is not lateness, and counting it as an enormous
 * number of late minutes would poison every total it appeared in.
 */
export function lateMinutesFor(
  day: string,
  checkInAt: Date | null | undefined,
  shift: ResolvedShift,
): number {
  if (!checkInAt) return 0;
  const threshold =
    shopDayStart(day).getTime() + (clockMinutes(shift.start) + shift.graceMinutes) * 60_000;
  return Math.max(0, Math.round((checkInAt.getTime() - threshold) / 60_000));
}

/* -------------------------------------------------------------------------------------------
 * The ledger: overtime and permission
 * ---------------------------------------------------------------------------------------- */

/**
 * The two things that are RECORDED rather than derived.
 *
 * `overtime` is time worked past the shift. `permission` is the Tamil-Nadu shop's own word for
 * a couple of hours off in the middle of the day — the bank, a school run, a doctor — and it is
 * genuinely not leave: the person came in, went out and came back, and the day is a working day
 * with a hole in it. A shop that had to record that as half a day's leave would be recording
 * something that did not happen.
 *
 * One collection with a kind rather than two collections, because the two rows are the same
 * row: a person, a day, a number of minutes and a reason. What differs is the sign they carry
 * into a wage conversation, and that is a question for whoever prices them.
 */
export const ATTENDANCE_LEDGER_KINDS = ['overtime', 'permission'] as const;
export type AttendanceLedgerKind = (typeof ATTENDANCE_LEDGER_KINDS)[number];
export const attendanceLedgerKindSchema = z.enum(ATTENDANCE_LEDGER_KINDS);

export const LEDGER_REASON_MAX_LENGTH = 300;
/** Sixteen hours. Longer than any real entry and short enough to catch a mistyped figure. */
export const MAX_LEDGER_MINUTES = 16 * 60;

const ledgerFieldsSchema = z.object({
  staffUserId: objectIdSchema,
  day: shopDaySchema,
  kind: attendanceLedgerKindSchema,
  /**
   * The two stamps, when the shop kept them. Optional because most of these are written from
   * memory the next morning — "she stayed back about two hours" — and refusing the entry
   * because nobody wrote down the exact minute would mean it is not recorded at all.
   */
  startAt: dateInputSchema.nullish(),
  endAt: dateInputSchema.nullish(),
  /**
   * The figure that counts. Sent even when the stamps are, because the stamps are a record of
   * what happened and this is what the shop agreed to pay for or deduct — and they are allowed
   * to differ, which is the whole reason this is not computed.
   */
  minutes: z.number().int().min(1).max(MAX_LEDGER_MINUTES),
  reason: z.string().trim().max(LEDGER_REASON_MAX_LENGTH).nullish(),
});

export const createAttendanceLedgerEntrySchema = ledgerFieldsSchema.refine(
  (value) => !value.startAt || !value.endAt || value.endAt.getTime() >= value.startAt.getTime(),
  { message: 'The end time must not be before the start time', path: ['endAt'] },
);
export type CreateAttendanceLedgerEntryInput = z.input<typeof createAttendanceLedgerEntrySchema>;

/** `staffUserId`, `day` and `kind` are absent: what an entry IS does not change. Correct it or
 * delete it. */
export const updateAttendanceLedgerEntrySchema = ledgerFieldsSchema
  .omit({ staffUserId: true, day: true, kind: true })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateAttendanceLedgerEntryInput = z.input<typeof updateAttendanceLedgerEntrySchema>;

export const attendanceLedgerEntrySchema = z.object({
  id: objectIdSchema,
  staffUserId: objectIdSchema,
  staffName: z.string(),
  staffEmail: z.string(),
  staffRole: userRoleSchema,
  day: shopDaySchema,
  kind: attendanceLedgerKindSchema,
  startAt: isoDateStringSchema.nullable(),
  endAt: isoDateStringSchema.nullable(),
  minutes: z.number().int().nonnegative(),
  reason: z.string().nullable(),
  recordedByUserId: z.string().nullable(),
  recordedByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type AttendanceLedgerEntry = z.infer<typeof attendanceLedgerEntrySchema>;

/** The longest span one ledger request may ask for. A shop-year, like the attendance history. */
export const MAX_LEDGER_RANGE_DAYS = 366;

export const attendanceLedgerQuerySchema = paginationQuerySchema
  .extend({
    staffUserId: objectIdSchema.optional(),
    kind: attendanceLedgerKindSchema.optional(),
    from: shopDaySchema,
    to: shopDaySchema,
  })
  .superRefine((value, ctx) => {
    if (value.from > value.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The start date must not be after the end date',
        path: ['from'],
      });
      return;
    }
    const spanDays =
      Math.round(
        (shopDayStart(value.to).getTime() - shopDayStart(value.from).getTime()) / 86_400_000,
      ) + 1;
    if (spanDays > MAX_LEDGER_RANGE_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Choose a shorter period — the ledger covers at most a year at a time',
        path: ['to'],
      });
    }
  });
export type AttendanceLedgerQuery = z.infer<typeof attendanceLedgerQuerySchema>;

/**
 * The totals under the ledger. ACROSS THE WHOLE RANGE, not the page — the same rule the day
 * summary and the attendance history follow, and for the same reason: page 2 of a month must
 * not report a month's overtime as the eight hours that fit on screen.
 */
export const attendanceLedgerTotalsSchema = z.object({
  overtimeMinutes: z.number().int().nonnegative(),
  permissionMinutes: z.number().int().nonnegative(),
  entries: z.number().int().nonnegative(),
});
export type AttendanceLedgerTotals = z.infer<typeof attendanceLedgerTotalsSchema>;

export const attendanceLedgerPageSchema = z.object({
  items: z.array(attendanceLedgerEntrySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  /** Range-wide and NOT page-scoped — see {@link attendanceLedgerTotalsSchema}. */
  totals: attendanceLedgerTotalsSchema,
});
export type AttendanceLedgerPage = z.infer<typeof attendanceLedgerPageSchema>;

/* -------------------------------------------------------------------------------------------
 * Leave requests
 * ---------------------------------------------------------------------------------------- */

/**
 * What kind of leave is being asked for.
 *
 * Short, and none of these is priced here. What a shop pays for a sick day is a payroll
 * decision that differs by shop and by person; what this enum does is let the owner see, at a
 * glance, whether the four days they are approving are the kind they were expecting.
 */
export const LEAVE_TYPES = ['casual', 'sick', 'unpaid', 'other'] as const;
export type LeaveType = (typeof LEAVE_TYPES)[number];
export const leaveTypeSchema = z.enum(LEAVE_TYPES);

/**
 * Where a request has got to.
 *
 * `cancelled` is the requester withdrawing it, and it is a separate state from `rejected` on
 * purpose: "she changed her mind" and "the shop said no" are different facts, and a shop
 * looking back at a pattern of refused leave should not be reading withdrawals as refusals.
 */
export const LEAVE_REQUEST_STATUSES = ['pending', 'approved', 'rejected', 'cancelled'] as const;
export type LeaveRequestStatus = (typeof LEAVE_REQUEST_STATUSES)[number];
export const leaveRequestStatusSchema = z.enum(LEAVE_REQUEST_STATUSES);

/** Still waiting on somebody. What the owner's badge counts and the list defaults to. */
export function isPendingLeave(status: LeaveRequestStatus): boolean {
  return status === 'pending';
}

export const LEAVE_REASON_MAX_LENGTH = 300;
/** The longest run of days one request may cover. A month off is a conversation, not a form. */
export const MAX_LEAVE_DAYS = 60;

export const createLeaveRequestSchema = z
  .object({
    /**
     * Whose leave. Optional, and absent means the caller's own — which is what a karigar's own
     * request always is. A manager filing one on somebody's behalf names them.
     */
    staffUserId: objectIdSchema.optional(),
    fromDay: shopDaySchema,
    toDay: shopDaySchema,
    type: leaveTypeSchema.default('casual'),
    reason: z.string().trim().min(1, 'Say why the leave is needed').max(LEAVE_REASON_MAX_LENGTH),
  })
  .superRefine((value, ctx) => {
    if (value.fromDay > value.toDay) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'The first day must not be after the last',
        path: ['fromDay'],
      });
      return;
    }
    if (leaveDayCount(value.fromDay, value.toDay) > MAX_LEAVE_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Ask for a shorter run of days, or speak to the shop directly',
        path: ['toDay'],
      });
    }
  });
export type CreateLeaveRequestInput = z.input<typeof createLeaveRequestSchema>;

/** Inclusive of both ends: one day off is a count of one, not zero. */
export function leaveDayCount(fromDay: string, toDay: string): number {
  return (
    Math.round((shopDayStart(toDay).getTime() - shopDayStart(fromDay).getTime()) / 86_400_000) + 1
  );
}

/**
 * Every day the request covers, in order. What approval marks on the roster.
 *
 * Through `addShopDays` rather than by slicing an ISO string off an added timestamp: a shop day
 * starts at midnight in the SHOP's timezone, so the UTC instant behind `2026-08-20` is the
 * evening of the 19th, and `toISOString().slice(0, 10)` would name every day one earlier.
 */
export function leaveDays(fromDay: string, toDay: string): string[] {
  const days: string[] = [];
  for (let index = 0; index < leaveDayCount(fromDay, toDay); index += 1) {
    days.push(addShopDays(fromDay, index));
  }
  return days;
}

/**
 * The decision. A note rides with both outcomes, and it is optional for both: "yes, but be back
 * for the Saturday deliveries" is as useful a thing to record as a refusal's reason.
 */
export const decideLeaveRequestSchema = z.object({
  approve: z.boolean(),
  note: z.string().trim().max(LEAVE_REASON_MAX_LENGTH).nullish(),
});
export type DecideLeaveRequestInput = z.input<typeof decideLeaveRequestSchema>;

export const leaveRequestSchema = z.object({
  id: objectIdSchema,
  staffUserId: objectIdSchema,
  staffName: z.string(),
  staffEmail: z.string(),
  staffRole: userRoleSchema,
  fromDay: shopDaySchema,
  toDay: shopDaySchema,
  dayCount: z.number().int().positive(),
  type: leaveTypeSchema,
  reason: z.string(),
  status: leaveRequestStatusSchema,
  requestedByUserId: z.string().nullable(),
  requestedByName: z.string().nullable(),
  decidedByUserId: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: isoDateStringSchema.nullable(),
  decisionNote: z.string().nullable(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type LeaveRequest = z.infer<typeof leaveRequestSchema>;

export const leaveRequestQuerySchema = paginationQuerySchema.extend({
  staffUserId: objectIdSchema.optional(),
  status: leaveRequestStatusSchema.optional(),
  /** Everything still waiting on a decision — the owner's default view. */
  pendingOnly: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((value) => value === true || value === 'true' || value === '1'),
});
export type LeaveRequestQuery = z.infer<typeof leaveRequestQuerySchema>;

export const leaveRequestPageSchema = z.object({
  items: z.array(leaveRequestSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
  /** How many are still waiting, across the whole shop — the badge, not the page. */
  pendingCount: z.number().int().nonnegative(),
});
export type LeaveRequestPage = z.infer<typeof leaveRequestPageSchema>;
